import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaCheck } from "react-icons/fa";
import { FiEdit } from "react-icons/fi";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import AmbulanceCallForm from "./AddCallAmbulance";
import PageHeading from "../../PageHeading";
import ExportOptions from "../../ExportOptions";
import ConfirmActionModal from "../../ConfirmDeleteModal";
import { Link } from "react-router-dom";
import TableSkeleton from "../../TableSkeleton";

const API_URL = "https://api.muktihospital.com/api/ambulance-call";
const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const AmbulanceCallList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [addCallModalOpen, setAddCallModalOpen] = useState(false);
  const [editCallId, setEditCallId] = useState(null);
  const [editCallData, setEditCallData] = useState(null);
  const [calls, setCalls] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const itemsPerPage = 10;
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const actionButtonClass =
    "size-7 rounded-full text-sm flex items-center justify-center transition-all duration-300";

  const fetchCalls = useCallback(async () => {
    setIsLoading(true);
    try {
      const url = `${API_URL}?search=${debouncedSearchQuery}&page=${currentPage}&limit=${itemsPerPage}`;
      const response = await axios.get(url, {
        headers: {
          "x-api-key": API_KEY,
        },
      });
      const list = Array.isArray(response.data) ? response.data : response.data.data || [];
      const total = response.data.total || list.length;
      setCalls(list);
      setTotalPages(Math.ceil(total / itemsPerPage) || 1);
      setTotalRecords(total || 0);
    } catch (err) {
      console.error("Error fetching ambulance calls:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      toast.error(err.response?.data?.error || "Failed to load ambulance calls.", {
        autoClose: 2000,
      });
      setCalls([]);
      setTotalPages(1);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, debouncedSearchQuery, itemsPerPage]);

  const handleRefreshCalls = useCallback(
    async (newCall = null) => {
      try {
        if (newCall) {
          setCalls((prevCalls) => {
            const callExists = prevCalls.some((call) => call.id === newCall.id);
            if (callExists) {
              return prevCalls.map((call) =>
                call.id === newCall.id ? { ...call, ...newCall } : call
              );
            }
            return [newCall, ...prevCalls];
          });
          setTotalRecords((prev) => prev + (newCall.id ? 0 : 1));
          setTotalPages((prev) =>
            Math.ceil((prev * itemsPerPage + (newCall.id ? 0 : 1)) / itemsPerPage)
          );
        } else {
          await fetchCalls();
        }
      } catch (err) {
        console.error("Error refreshing calls:", err);
        toast.error(err.response?.data?.error || "Failed to refresh calls.", {
          autoClose: 2000,
        });
        setCalls([]);
      }
    },
    [fetchCalls, itemsPerPage]
  );

  const handleDelete = useCallback(
    async (callIds) => {
      try {
        await Promise.all(
          callIds.map((id) =>
            axios.delete(`${API_URL}/${id}`, {
              headers: {
                "x-api-key": API_KEY,
              },
            })
          )
        );
        await handleRefreshCalls();
        setSelectedRows([]);
        toast.success(`${callIds.length} ambulance call(s) deleted successfully!`, {
          autoClose: 2000,
        });
      } catch (err) {
        console.error("Error deleting calls:", {
          message: err.message,
          response: err.response?.data,
        });
        toast.error(err.response?.data?.error || "Failed to delete call(s).", {
          autoClose: 2000,
        });
      }
    },
    [handleRefreshCalls]
  );

  const handleEdit = useCallback((call) => {
    try {
      if (!call?.id) {
        toast.error("Invalid call ID", { autoClose: 2000 });
        return;
      }
      setEditCallId(call.id);
      setEditCallData({
        id: call.id || "",
        caseId: call.caseId || "",
        patientId: call.patientId || call.patient?.id || "",
        patientName: call.patientName?.trim() || call.patient?.name || "",
        vehicleModel: call.vehicleModel?.trim() || "",
        driverName: call.driverName?.trim() || "",
        chargeName: call.chargeName || "",
        standardCharge: call.standardCharge?.toString() || "0",
        discount: call.discount?.toString() || "0",
        tax: call.tax?.toString() || "0",
        paymentAmount: call.paymentAmount?.toString() || "0",
        paymentMode: call.paymentMode || "cash",
        date: call.date ? new Date(call.date).toISOString().split("T")[0] : "",
        note: call.note || "",
      });
      setAddCallModalOpen(true);
    } catch (err) {
      console.error("Error fetching call for edit:", {
        message: err.message,
        response: err.response?.data,
      });
      toast.error(err.response?.data?.error || "Failed to load call for editing.", {
        autoClose: 2000,
      });
    }
  }, []);

  useEffect(() => {
    fetchCalls();
  }, [fetchCalls]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery]);

  useEffect(() => {
    if (debouncedSearchQuery && calls.length === 0 && !isLoading) {
      toast.info("No calls found", { toastId: "no-calls-toast", autoClose: 2000 });
    }
  }, [debouncedSearchQuery, calls, isLoading]);

  const handlePageChange = useCallback(
    (page) => {
      if (page > 0 && page <= totalPages) {
        setCurrentPage(page);
        setSelectedRows([]);
      }
    },
    [totalPages]
  );

  const handleSelectAll = useCallback(
    (e) => {
      const validIds = calls
        .filter((call) => call.id != null)
        .map((call) => call.id);
      setSelectedRows(e.target.checked ? validIds : []);
    },
    [calls]
  );

  const handleRowSelect = useCallback((callId) => {
    if (callId == null) return;
    setSelectedRows((prev) =>
      prev.includes(callId)
        ? prev.filter((id) => id !== callId)
        : [...prev, callId]
    );
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const headers = [
    "Serial No",
    "Case ID",
    "Patient",
    "Vehicle Model",
    "Charge Name",
    "Payment Amount (৳)",
    "Net Amount (৳)",
    "Date",
  ];

  const skeletonColumns = [
    { width: 20, height: 20, isCircle: true, className: "print-hidden-in-print" },
    { width: 60, height: 16 },
    { width: 80, height: 16 },
    { width: 150, height: 16 },
    { width: 150, height: 16 },
    { width: 150, height: 16 },
    { width: 150, height: 16 },
    { width: 100, height: 16 },
    { width: 100, height: 16 },
    { width: 120, height: 16, className: "print-hidden-in-print" },
  ];

  const dataMapper = (call, index) => [
    (currentPage - 1) * itemsPerPage + calls.indexOf(call) + 1,
    call.caseId || "N/A",
    call.patientName || "N/A",
    call.vehicleModel || "N/A",
    call.chargeName || "N/A",
    (call.paymentAmount || 0),
    call.netAmount || "N/A",
    call.date
      ? new Date(call.date).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "N/A",
  ].join("\t");

  const breadcrumbs = useMemo(
    () => [
      { label: "Dashboard", url: "/" },
      { label: "Ambulance" },
      { label: "Ambulance Call List" },
    ],
    []
  );

  const allVisibleSelected = useMemo(
    () =>
      calls.length > 0 &&
      calls.every((call) => call.id && selectedRows.includes(call.id)),
    [calls, selectedRows]
  );

  return (
    <div>
      <ToastContainer position="top-right" className="print-hidden" />
      <div className="print-hidden">
        <PageHeading title="Ambulance Call List" breadcrumbs={breadcrumbs} />
      </div>
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Ambulance Call List
            </h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => setDeleteModalOpen(true)}
                className={`${actionButtonClass} bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white`}
                aria-label="Delete selected calls"
                title="Delete selected calls"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            <input
              type="text"
              className="max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search by patient name"
              value={searchQuery}
              onChange={handleSearchChange}
              aria-label="Search calls by patient name"
            />
            <div className="flex items-center justify-between w-full gap-3">
              <button
                onClick={() => {
                  setEditCallId(null);
                  setEditCallData(null);
                  setAddCallModalOpen(true);
                }}
                className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
                aria-label="Add new ambulance call"
              >
                + Add Ambulance Call
              </button>
              <Link to="/ambulance">
                <button
                  className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
                  title="View Ambulance List"
                  aria-label="View ambulance list"
                >
                  Ambulance List
                </button>
              </Link>
              <ExportOptions
                data={calls}
                dropdownOpen={dropdownOpen}
                toggleDropdown={toggleDropdown}
                title="Ambulance Call Reports"
                headers={headers}
                dataMapper={dataMapper}
              />
            </div>
          </div>
        </div>
        <style>
          {`
            @media print {
              .print-hidden, .print-hidden-in-print {
                display: none !important;
              }
              .print-all-rows {
                display: table-row !important;
              }
              .print-table {
                width: 100%;
                border-collapse: collapse;
              }
              .print-table th, .print-table td {
                border: 1px solid #000;
                padding: 8px;
                text-align: left;
                font-size: 12px;
              }
              .print-table th {
                background-color: #f0f0f0;
                font-weight: bold;
              }
              .print-table tbody tr:nth-child(even) {
                background-color: #f9f9f9;
              }
            }
          `}
        </style>
        <div className="overflow-x-auto">
          <div className="print-wrapper">
            <table
              className="min-w-[1440px] w-full divide-y divide-gray-200 print-table"
              role="table"
            >
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left print-hidden-in-print" scope="col">
                    <div className="relative left-3 -top-[2px]">
                      <input
                        type="checkbox"
                        id="selectAll"
                        className="hidden peer print-hidden"
                        onChange={handleSelectAll}
                        checked={allVisibleSelected}
                        disabled={calls.length === 0}
                        aria-label="Select all calls on this page"
                      />
                      <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                        <FaCheck className="text-slate-100" />
                      </span>
                      <label
                        htmlFor="selectAll"
                        className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                      ></label>
                    </div>
                  </th>
                  {headers.map((header, idx) => (
                    <th
                      key={idx}
                      className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3"
                      scope="col"
                    >
                      {header}
                    </th>
                  ))}
                  <th
                    className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print"
                    scope="col"
                  >
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <TableSkeleton
                    rowCount={5}
                    columns={skeletonColumns}
                    baseColor="#e5e7eb"
                    highlightColor="#f3f4f6"
                  />
                ) : calls.length === 0 ? (
                  <tr className="print-hidden">
                    <td
                      colSpan={9}
                      className="text-center py-5 text-gray-500"
                      role="cell"
                    >
                      {searchQuery
                        ? "No calls found matching your search."
                        : "No calls available."}
                    </td>
                  </tr>
                ) : (
                  calls.map((call, index) => (
                    <tr
                      key={call.id}
                      className="border-b hover:bg-gray-50"
                      role="row"
                    >
                      <td
                        className="text-left capitalize font-inter font-normal text-sm text-M-heading-color print-hidden-in-print"
                        role="cell"
                      >
                        <div className="relative left-3 -top-[2px]">
                          <input
                            type="checkbox"
                            id={`call-${index}`}
                            className="hidden peer"
                            checked={selectedRows.includes(call.id)}
                            onChange={() => handleRowSelect(call.id)}
                            aria-label={`Select call ${call.caseId || "unknown"}`}
                          />
                          <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                            <FaCheck className="text-slate-100" />
                          </span>
                          <label
                            htmlFor={`call-${index}`}
                            className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                          ></label>
                        </div>
                      </td>
                      <td
                        className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3"
                        role="cell"
                      >
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </td>
                      <td
                        className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3"
                        role="cell"
                      >
                        {call.caseId || "N/A"}
                      </td>
                      <td
                        className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3"
                        role="cell"
                      >
                        {call.patientName || "N/A"}
                      </td>
                      <td
                        className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3"
                        role="cell"
                      >
                        {call.vehicleModel || "N/A"}
                      </td>
                      <td
                        className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3"
                        role="cell"
                      >
                        {call.chargeName || "N/A"}
                      </td>
                      <td
                        className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3"
                        role="cell"
                      >
                        {call.paymentAmount || 0}
                      </td>
                      <td
                        className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3"
                        role="cell"
                      >
                        {call.netAmount || "N/A"}
                      </td>
                      <td
                        className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3"
                        role="cell"
                      >
                        {call.date
                          ? new Date(call.date).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })
                          : "N/A"}
                      </td>
                      <td
                        className="p-3 text-left space-x-2 print-hidden-in-print flex shrink-0"
                        role="cell"
                      >
                        <button
                          onClick={() => handleEdit(call)}
                          className={`${actionButtonClass} bg-M-primary-color/20 text-M-primary-color hover:bg-M-primary-color hover:text-white`}
                          aria-label={`Edit call ${call.caseId || "unknown"}`}
                          title="Edit"
                        >
                          <FiEdit />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedRows([call.id]);
                            setDeleteModalOpen(true);
                          }}
                          className={`${actionButtonClass} bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white border`}
                          aria-label={`Delete call ${call.caseId || "unknown"}`}
                          title="Delete"
                        >
                          <RiDeleteBin6Line />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="p-5 text-end print-hidden">
              <div className="inline-flex items-center justify-between w-full">
                <p className="text-sm text-gray-600">
                  Records: {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, totalRecords)} of{" "}
                  {totalRecords}
                </p>
                <nav aria-label="Pagination">
                  <ul className="inline-flex items-center justify-end border-collapse border border-M-text-color/20 rounded-md overflow-hidden">
                    <li>
                      <button
                        className={`table-pagination-item ${
                          currentPage === 1 ? "disabled" : ""
                        }`}
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        aria-label="Previous page"
                        aria-disabled={currentPage === 1}
                      >
                        <MdKeyboardArrowLeft />
                      </button>
                    </li>
                    {totalPages <= 10 ? (
                      [...Array(totalPages)].map((_, i) => (
                        <li key={i}>
                          <button
                            className={`table-pagination-item ${
                              currentPage === i + 1 ? "bg-M-primary-color text-white" : ""
                            }`}
                            onClick={() => handlePageChange(i + 1)}
                            aria-label={`Go to page ${i + 1}`}
                            aria-current={currentPage === i + 1 ? "page" : undefined}
                          >
                            {i + 1}
                          </button>
                        </li>
                      ))
                    ) : (
                      (() => {
                        const pages = [];
                        const firstPages = 5;
                        const lastPages = 3;
                        for (let i = 1; i <= Math.min(firstPages, totalPages); i++) {
                          pages.push(
                            <li key={i}>
                              <button
                                className={`table-pagination-item ${
                                  currentPage === i ? "bg-M-primary-color text-white" : ""
                                }`}
                                onClick={() => handlePageChange(i)}
                                aria-label={`Go to page ${i}`}
                                aria-current={currentPage === i ? "page" : undefined}
                              >
                                {i}
                              </button>
                            </li>
                          );
                        }
                        if (totalPages > firstPages + lastPages) {
                          pages.push(
                            <li key="ellipsis" className="table-pagination-item">
                              ...
                            </li>
                          );
                        }
                        for (
                          let i = Math.max(firstPages + 1, totalPages - lastPages + 1);
                          i <= totalPages;
                          i++
                        ) {
                          pages.push(
                            <li key={i}>
                              <button
                                className={`table-pagination-item ${
                                  currentPage === i ? "bg-M-primary-color text-white" : ""
                                }`}
                                onClick={() => handlePageChange(i)}
                                aria-label={`Go to page ${i}`}
                                aria-current={currentPage === i ? "page" : undefined}
                              >
                                {i}
                              </button>
                            </li>
                          );
                        }
                        return pages;
                      })()
                    )}
                    <li>
                      <button
                        className={`table-pagination-item ${
                          currentPage === totalPages ? "disabled" : ""
                        }`}
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        aria-label="Next page"
                        aria-disabled={currentPage === totalPages}
                      >
                        <MdKeyboardArrowRight />
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          )}
        </div>
      </div>
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedRows([]);
        }}
        onConfirm={() => handleDelete(selectedRows)}
        ids={selectedRows}
        entityName="ambulance call"
        title="Confirm Delete"
        confirmText="Delete"
        cancelText="Cancel"
        message={
          Array.isArray(selectedRows) && selectedRows.length > 1
            ? `Do you really want to delete ${selectedRows.length} ambulance call(s)?`
            : `Do you really want to delete this ambulance call?`
        }
      />
      <AmbulanceCallForm
        isOpen={addCallModalOpen}
        onClose={() => {
          setAddCallModalOpen(false);
          setEditCallId(null);
          setEditCallData(null);
        }}
        onSuccess={async (newCall) => {
          await handleRefreshCalls(newCall);
        }}
        initialData={editCallData}
        callId={editCallId}
      />
    </div>
  );
};

export default AmbulanceCallList;